import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CompaniesClient,
  CompanyQPViewModelCompanyQPTotalTypePagedResultResponse,
  EditUserPoliciesAndRolesCommand,
  EditUserProfileCommand,
  ICurrentUserViewModel,
  IFillSiteQPSearchViewModelTotalTypePagedResultResponse,
  IFillSiteUserSearchViewModelTotalTypePagedResultResponse,
  InviteLegalEntityCommand,
  InviteQualifiedPersonCommand,
  InviteQualifiedPersonListCommand,
  InviteUserCommand,
  ISourceSiteQPSearchViewModelTotalTypePagedResultResponse,
  ISourceSiteUserSearchViewModelTotalTypePagedResultResponse,
  LegalEntitiesClient,
  QualifiedPersonInviteViewModel,
  QualifiedPersonsClient,
  QualifiedPersonSearchViewModelTotalTypePagedResultResponse,
  UserInviteViewModel,
  UsersClient,
  UserSearchViewModelCleanFillUserTotalTypePagedResultResponse
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { SearchQuery } from '@view-models/search-query/search-query';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class HttpUsersService {
  constructor(
    private http: HttpClient,
    private userClient: UsersClient,
    private qpClient: QualifiedPersonsClient,
    private companiesClient: CompaniesClient,
    private qualifiedPersonsClient: QualifiedPersonsClient,
    private legalEntitiesClient: LegalEntitiesClient
  ) { }

  getActiveUser(): Observable<ICurrentUserViewModel> {
    return this.userClient.getCurrentUserDetails(new Date().getTimezoneOffset().toString());
  }

  getUsers(query: ISearchQuery): Observable<UserSearchViewModelCleanFillUserTotalTypePagedResultResponse> {
    return this.userClient.searchUsers(query.Page, query.PageSize, query.SortBy, query.FilterBy, new Date().getTimezoneOffset().toString());
  }

  getApproveUsers(query: ISearchQuery): Observable<UserSearchViewModelCleanFillUserTotalTypePagedResultResponse> {
    return this.userClient.searchApproveUsers(
      query.Page, query.PageSize, query.SortBy, query.FilterBy, new Date().getTimezoneOffset().toString());
  }

  getQPUsers(userUniqueId: string, query: ISearchQuery): Observable<CompanyQPViewModelCompanyQPTotalTypePagedResultResponse> {
    return this.companiesClient.searchCompanyQPs(
      userUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }
  getAllQPUsers(query: ISearchQuery): Observable<QualifiedPersonSearchViewModelTotalTypePagedResultResponse> {
    return this.qualifiedPersonsClient.searchQualifiedPerson(
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }

  deactivateUser(userUniqueId: string): Observable<void> {
    return this.userClient.deactivateUser(userUniqueId, undefined);
  }

  approveUser(userUniqueId: string): Observable<void> {
    return this.userClient.approveUser(userUniqueId, undefined);
  }

  deleteUserForever(userUniqueId: string): Observable<void> {
    return this.userClient.deleteUserForever(userUniqueId, undefined);
  }

  deleteUser(userUniqueId: string): Observable<void> {
    return this.userClient.deleteUser(userUniqueId, undefined);
  }

  getUser(userUniqueId: string) {
    return this.userClient.getUserDetails(userUniqueId, undefined);
  }
  getQualifiedPerson(uniqueId: string) {
    return this.qpClient.getQualifiedPersonDetail(uniqueId, new Date().getTimezoneOffset().toString());
  }

  editUserRolesAndPermissions(payload: EditUserPoliciesAndRolesCommand) {
    return this.userClient.editUserPoliciesAndRoles(payload.userUniqueId, undefined, payload);
  }

  editUserProfile(userUniqueId: string, payload: EditUserProfileCommand): Observable<void> {
    return this.userClient.editUserProfile(userUniqueId, undefined, payload);
  }

  inviteUser(legalEntityUniqueId: string, payload: InviteUserCommand): Observable<UserInviteViewModel> {
    return this.legalEntitiesClient.inviteNewUser(legalEntityUniqueId, undefined, payload);
  }
  inviteQPUserById(legalEntityUniqueId: string, payload: InviteQualifiedPersonListCommand): Observable<void> {
    return this.legalEntitiesClient.inviteNewQualifiedPersonList(legalEntityUniqueId, new Date().getTimezoneOffset().toString(), payload);
  }
  inviteQPUserWithAccess(legalEntityUniqueId: string, payload: InviteQualifiedPersonCommand): Observable<void> {
    return this.legalEntitiesClient.inviteNewQualifiedPerson(legalEntityUniqueId, new Date().getTimezoneOffset().toString(), payload);
  }
  removeQPUserAccess(uniqueId: string, qpUniqueId: string): Observable<void> {
    return this.legalEntitiesClient.removeQPAdmin(uniqueId, qpUniqueId, new Date().getTimezoneOffset().toString());
  }

  inviteQPUser(payload: InviteLegalEntityCommand): Observable<QualifiedPersonInviteViewModel> {
    return this.legalEntitiesClient.inviteNewCompanyQP(new Date().getTimezoneOffset().toString(), payload);
  }

  hasDuplicateEmail(email: string): Observable<boolean> {
    const queryQP = new SearchQuery()
      .setPageIndex(1)
      .setPageSize(1)
      .appendFilterBy({ property: 'contact', contains: email });
    const query = new SearchQuery()
      .setPageIndex(1)
      .setPageSize(1)
      .appendFilterBy({ property: 'email', equal: email });
    return combineLatest([
      this.qualifiedPersonsClient.searchQualifiedPerson(
        queryQP.Page,
        queryQP.PageSize,
        queryQP.SortBy,
        queryQP.FilterBy,
        new Date().getTimezoneOffset().toString()
      ),
      this.userClient.searchUsers(query.Page, query.PageSize, query.SortBy, query.FilterBy, undefined)
    ]).pipe(
      map(([qps, employees]) => {
        return qps?.records?.length > 0 || employees.records?.length > 0;
      })
    );
  }

  userSourceSitesSearch(userUniqueId: string, query: ISearchQuery): Observable<ISourceSiteUserSearchViewModelTotalTypePagedResultResponse> {
    return this.userClient.userSourceSitesSearch(
      userUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }
  qpSourceSitesSearch(userUniqueId: string, query: ISearchQuery): Observable<ISourceSiteQPSearchViewModelTotalTypePagedResultResponse> {
    return this.qpClient.qPSourceSitesSearch(
      userUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }

  userFillSiteSearch(userUniqueId: string, query: ISearchQuery): Observable<IFillSiteUserSearchViewModelTotalTypePagedResultResponse> {
    return this.userClient.userFillSitesSearch(
      userUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }
  qpFillSiteSearch(userUniqueId: string, query: ISearchQuery): Observable<IFillSiteQPSearchViewModelTotalTypePagedResultResponse> {
    return this.qpClient.qPFillSitesSearch(
      userUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }
}
